import { Injectable } from '@angular/core';
import { collection, doc, setDoc, getDoc, updateDoc, deleteDoc, getDocs, CollectionReference, DocumentData, query, where, QuerySnapshot, Timestamp } from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { formatLocation } from 'app/utils/flight/flight-data-formatters';

export interface FlightWatcher {
    id?: string;
    userId: string;
    flightOptionId: string;
    flightOptionFingerprint: string;
    displayName: string;
    expiresOn: Date | Timestamp | null;
    createdAt: Date | Timestamp;
    updatedAt: Date | Timestamp;
    checkedAt: Date | Timestamp | null;
    discoveredFlights?: Array<{ id: string; discoveredAt: Date | Timestamp }>;
    isBringingPets?: boolean | null;
    isBringingChildren?: boolean | null;
    isPetOk?: boolean | null;
    isChildrenOk?: boolean | null;
}

@Injectable({ providedIn: 'root' })
export class FlightWatcherService {
    private collectionRef: CollectionReference<DocumentData>;

    constructor(private firebaseService: FirebaseService, private userService: UserService) {
        this.collectionRef = collection(this.firebaseService.firestore, 'flight-watchers');
    }

    private async getUserProfile(): Promise<any> {
        const user = await this.userService.getUserProfile();
        if (!user) throw new Error('User not authenticated.');
        return user;
    }

    async createFlightWatcher(flightOption: any): Promise<{ saved: boolean; id?: string; reason?: string }> {
        const userProfile = await this.getUserProfile();
        const fingerprint = await this.generateFingerprint(flightOption);

        const watchersRef = collection(this.firebaseService.firestore, 'flight-watchers');
        const q = query(watchersRef, where('userId', '==', userProfile.id), where('flightOptionFingerprint', '==', fingerprint));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            return { saved: false, reason: 'An Active Watcher already exists with these plans' };
        }

        const departureDate =
            flightOption.departureDateExact ||
            flightOption.departureDateRangeEnd;

        let expiresOn: Date | null = null;
        if (departureDate) {
            const d = new Date(departureDate);
            d.setHours(23, 59, 59, 999);
            expiresOn = d;
        }

        const fromRaw = flightOption.departureLocationName;
        let toRaw: string;

        if (flightOption.arrivalLocationName) {
            toRaw = flightOption.arrivalLocationName;
        } else {
            const generalType = flightOption.arrivalGeneralType || 'Location';
            const parts = [];

            if (flightOption.arrivalGeneralState) {
                parts.push(flightOption.arrivalGeneralState);
            }
            if (flightOption.arrivalGeneralCountry) {
                parts.push(flightOption.arrivalGeneralCountry);
            }

            const locationSuffix = parts.length > 0 ? ` in ${parts.join(', ')}` : '';
            toRaw = `Any ${generalType}${locationSuffix}`;
        }

        const flightDisplayName = `${formatLocation(fromRaw)} to ${formatLocation(toRaw)}`;

        const firestoreDoc = {
            userId: userProfile.id,
            flightOptionId: flightOption.id,
            flightOptionFingerprint: fingerprint,
            displayName: flightDisplayName,
            expiresOn,
            createdAt: new Date(),
            updatedAt: new Date(),
            checkedAt: null,
            isBringingPets: flightOption.isBringingPets ?? null,
            isBringingChildren: flightOption.isBringingChildren ?? null,
            isPetOk: flightOption.isPetOk ?? null,
            isChildrenOk: flightOption.isChildrenOk ?? null
        };

        const docRef = doc(this.collectionRef);
        await setDoc(docRef, firestoreDoc);

        return { saved: true, id: docRef.id };
    }


    async getFlightWatcher(watcherId: string): Promise<FlightWatcher | null> {
        const docRef = doc(this.collectionRef, watcherId);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            return null;
        }
        return this.withDiscoveries({ id: watcherId, ...snapshot.data() } as FlightWatcher);
    }

    async updateFlightWatcher(watcherId: string, updates: Partial<FlightWatcher>): Promise<void> {
        const docRef = doc(this.collectionRef, watcherId);
        await updateDoc(docRef, updates);
    }

    async deleteFlightWatcher(watcherId: string): Promise<void> {
        const docRef = doc(this.collectionRef, watcherId);
        await deleteDoc(docRef);
    }

    async getAllFlightWatchers(): Promise<FlightWatcher[]> {
        const snapshot = await getDocs(this.collectionRef);
        return Promise.all(snapshot.docs.map(doc => this.withDiscoveries({ id: doc.id, ...doc.data() } as FlightWatcher)));
    }

    async getFlightWatchersByUserId(userId: string): Promise<FlightWatcher[]> {
        const q = query(this.collectionRef, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        return Promise.all(snapshot.docs.map(doc => this.withDiscoveries({ id: doc.id, ...doc.data() } as FlightWatcher)));
    }


    // **** NEW UTILITIES

    async generateFingerprint(flightOption: any): Promise<string> {
        const relevantData = this.extractRelevantFields(flightOption);
        const jsonString = JSON.stringify(relevantData);
        const encoder = new TextEncoder();
        const data = encoder.encode(jsonString);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
    }

    private extractRelevantFields(option: any): Record<string, unknown> {
        // The fields below define the uniqueness of a watcher.  They are serialized
        // and hashed in `generateFingerprint` to prevent duplicate watchers with the
        // same search criteria.
        return {
            departureLocationName: option.departureLocationName,
            arrivalLocationName: option.arrivalLocationName,
            arrivalGeneralType: option.arrivalGeneralType,
            arrivalGeneralState: option.arrivalGeneralState,
            arrivalGeneralCountry: option.arrivalGeneralCountry,
            departureDateExact: option.departureDateExact,
            departureDateRangeStart: option.departureDateRangeStart ?? option.departureDateRange?.start,
            departureDateRangeEnd: option.departureDateRangeEnd ?? option.departureDateRange?.end,
            departureTimeExact: option.departureTimeExact,
            departureTimeRangeStart: option.departureTimeRangeStart ?? option.departureTimeRange?.start,
            departureTimeRangeEnd: option.departureTimeRangeEnd ?? option.departureTimeRange?.end,
            seatsNeeded: option.seatsNeeded,
        };
    }

    private async withDiscoveries(watcher: FlightWatcher): Promise<FlightWatcher> {
        if (!watcher.id) {
            return watcher;
        }
        const discoveriesRef = collection(this.firebaseService.firestore, 'flight-watchers', watcher.id, 'discoveries');
        const snapshot = await getDocs(discoveriesRef);
        return {
            ...watcher,
            discoveredFlights: snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: data['flightId'] || doc.id,
                    discoveredAt: data['discoveredAt']
                };
            })
        };
    }

}
